import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import { podeApagar } from "../lib/desfazer";
import { brl } from "../lib/formato";

const virgula = (v) => (Number(v) || 0).toFixed(2).replace(".", ",");
const numero = (texto) => parseFloat(String(texto).replace(",", "."));

// Na divisão, o campo edita a parte da vez: "o segundo quer pagar R$ 10" e as
// partes que faltam dividem o resto (dono, 24/09/2026)
function CampoParte({ parte, onEditarParte, compacto, classe }) {
  const [texto, setTexto] = useState(virgula(parte.valor));
  useEffect(() => { setTexto(virgula(parte.valor)); }, [parte.valor, parte.numero]);
  const aplicar = (e) => {
    const num = numero(e.target.value);
    if (!Number.isFinite(num) || Math.abs(num - parte.valor) < 0.005) { setTexto(virgula(parte.valor)); return; }
    if (!onEditarParte(parte.numero - 1, num)) setTexto(virgula(parte.valor));
  };
  return (
    <input
      type="text"
      inputMode="decimal"
      value={texto}
      onFocus={(e) => e.target.select()}
      onChange={(e) => setTexto(e.target.value.replace(/[^\d.,]/g, ""))}
      onBlur={aplicar}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      className={`${classe} border-b-2 border-dashed border-amber-500/40 focus:border-amber-500`}
      title="Clique para mudar o valor desta parte"
      aria-label={compacto ? "Valor da parte" : "Valor da parte da divisão"}
      data-e2e="valor-parte"
    />
  );
}

// Campo editável do valor: editar à mão é o jeito de receber só uma parte
// "por valor" — ou só um pedaço dos produtos marcados. Na divisão, edita a parte.
export function CampoValor({ total, travado, compacto, parte, onEditarParte }) {
  const { calculado, override, setOverride } = total;
  const classe = `${compacto ? "text-3xl w-36 text-left" : "text-5xl lg:text-6xl w-56 lg:w-72 text-center"} font-black text-primary tabular-nums bg-transparent outline-none`;
  if (travado && parte && onEditarParte) return <CampoParte parte={parte} onEditarParte={onEditarParte} compacto={compacto} classe={classe} />;
  return (
    <input
      type="text"
      inputMode="decimal"
      readOnly={travado}
      value={override !== "" && !travado ? override : virgula(calculado)}
      onFocus={(e) => { if (travado) return; if (override === "") setOverride(virgula(calculado)); e.target.select(); }}
      onChange={(e) => setOverride(e.target.value.replace(/[^\d.,]/g, ""))}
      onBlur={(e) => {
        if (travado) return;
        const num = numero(e.target.value);
        if (!Number.isFinite(num) || num < 0) { setOverride(""); return; }
        setOverride(Math.abs(num - calculado) < 0.005 ? "" : virgula(num));
      }}
      className={`${classe} ${travado ? "" : "border-b-2 border-dashed border-primary/30 focus:border-primary"}`}
      title={travado ? "Valor da parte da divisão" : "Clique para editar o valor a pagar"}
    />
  );
}

// Uma linha por pagamento lançado. A lixeira apaga o pagamento (e estorna a
// venda no caixa); PIX Online e o que o cliente pagou pelo celular não apagam.
function LinhaPagamento({ pl, pmVisual, onApagar }) {
  const [confirmando, setConfirmando] = useState(false);
  const [apagando, setApagando] = useState(false);
  const vis = pmVisual(pl.code || "", pl.label || pl.forma || "");
  const Icone = vis.icon;
  const apagavel = !!onApagar && podeApagar(pl);
  const apagar = async () => {
    setApagando(true);
    try {
      await onApagar(pl.id);
    } finally {
      setApagando(false);
      setConfirmando(false);
    }
  };
  if (confirmando) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-red-500/50 bg-red-500/10 px-3 py-2" data-e2e="confirmar-apagar-pagamento">
        <span className="text-sm font-medium flex-1 min-w-0">Apagar {pl.label || pl.code} de {brl(pl.value)}?</span>
        <button type="button" disabled={apagando} onClick={apagar} className="text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded px-2.5 py-1.5 disabled:opacity-50">
          {apagando ? "Apagando…" : "Apagar"}
        </button>
        <button type="button" disabled={apagando} onClick={() => setConfirmando(false)} className="text-xs text-muted-foreground hover:underline px-1">Não</button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2.5 rounded-lg bg-background/60 border border-border px-3 py-2">
      <span className={`p-1 rounded ${vis.color} text-white shrink-0 flex items-center justify-center`}><Icone className="w-3.5 h-3.5" /></span>
      <span className="text-sm font-medium flex-1 min-w-0 truncate">
        {pl.label || pl.forma || pl.code}
        {pl.parte && <span className="text-muted-foreground"> · parte {pl.parte}</span>}
      </span>
      {Number(pl.troco_valor) > 0 && <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">troco {brl(pl.troco_valor)}</span>}
      <span className="text-sm font-bold tabular-nums shrink-0">{brl(pl.value)}</span>
      {apagavel && (
        <button type="button" onClick={() => setConfirmando(true)} className="p-1 -mr-1 rounded text-muted-foreground hover:text-red-600 hover:bg-red-500/10 shrink-0"
          title="Apagar este pagamento" aria-label={`Apagar pagamento ${pl.label || pl.code}`} data-e2e="apagar-pagamento">
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export function LogPagamentos({ log, pmVisual, onApagar }) {
  if (!log.length) return null;
  return (
    <div className="mt-4 space-y-1.5 text-left max-w-xl mx-auto">
      {log.map((pl, i) => <LinhaPagamento key={pl.id || pl.pagamento_id || i} pl={pl} pmVisual={pmVisual} onApagar={onApagar} />)}
    </div>
  );
}

export default function TotalAPagar({ total, parte, selecionadosQtd, pmVisual, onEditarParte }) {
  const { subtotal, desconto, calculado, overrideAtivo, overrideNum, setOverride, pago, restante, log } = total;
  // Desconto concedido em pagamento anterior desta mesa: já está gravado e já
  // abate do Restante — não tem X, não se aplica de novo (achado 503)
  const gravado = Number(total.descontoGravado) || 0;
  const editado = !parte && overrideAtivo && Math.abs(overrideNum - calculado) > 0.005;
  let titulo = "Total a pagar";
  if (parte) titulo = `Parte ${parte.numero} de ${parte.total}`;
  else if (selecionadosQtd > 0) titulo = `${selecionadosQtd} produto(s)`;

  return (
    <div className="text-center p-6 lg:p-8 bg-muted rounded-2xl">
      <p className={`text-base ${parte ? "font-bold text-amber-600 dark:text-amber-400" : "text-muted-foreground"}`}>{titulo}</p>
      {desconto > 0 && <p className="text-xs text-muted-foreground line-through tabular-nums">{brl(subtotal)}</p>}
      <div className="flex items-center justify-center gap-1.5 mt-0.5">
        <span className="text-3xl lg:text-4xl font-bold text-primary">R$</span>
        <CampoValor total={total} travado={!!parte} parte={parte} onEditarParte={onEditarParte} />
      </div>
      {editado && (
        <div className="flex items-center justify-center gap-2 mt-1">
          <p className="text-[11px] text-amber-600 dark:text-amber-400 font-semibold">Valor editado · calculado: {brl(calculado)}</p>
          <button type="button" onClick={() => setOverride("")} className="text-[11px] text-primary underline hover:no-underline">Restaurar</button>
        </div>
      )}
      {editado && selecionadosQtd > 0 && overrideNum < calculado && (
        <p className="text-[11px] text-muted-foreground mt-0.5">Paga só esse valor dos produtos marcados; o resto deles continua em aberto.</p>
      )}
      {desconto > 0 && <p className="text-xs text-green-600 dark:text-green-400 font-semibold tabular-nums mt-1">- {brl(desconto)} de desconto</p>}
      {(pago > 0 || gravado > 0) && (
        <div className="mt-3 rounded-lg bg-green-500/10 border border-green-500/30 px-3 py-1.5 inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-0.5 mx-auto">
          <span className="text-sm font-semibold text-green-700 dark:text-green-400 tabular-nums">Pago: {brl(pago)}</span>
          {gravado > 0 && (
            <>
              <span className="text-xs text-muted-foreground">·</span>
              <span className="text-sm font-semibold text-green-700 dark:text-green-400 tabular-nums" title="Desconto concedido em pagamento anterior desta mesa: já gravado, já abatido do Restante" data-e2e="desconto-ja-aplicado">
                Desconto já aplicado: {brl(gravado)}
              </span>
            </>
          )}
          <span className="text-xs text-muted-foreground">·</span>
          <span className="text-sm font-semibold text-orange-600 dark:text-orange-400 tabular-nums">Restante: {brl(restante)}</span>
        </div>
      )}
      <LogPagamentos log={log} pmVisual={pmVisual} onApagar={total.onApagar} />
    </div>
  );
}
